import { getMaxStep } from './content';
import { createItemId, deriveTopThree, mergeImportedItems, todayIsoDate } from './ranking';
import type {
  BootAdvancedState,
  BootItem,
  BootMode,
  BootSession,
  BootTag,
  MorningBootState,
} from './types';
import { DEFAULT_MORNING_BOOT_STATE } from './types';

export function createSession(mode: BootMode, backlog: BootItem[]): BootSession {
  const backlogItems = backlog.map((item) => ({ ...item, id: createItemId() }));
  return {
    id: createItemId(),
    date: todayIsoDate(),
    mode,
    step: 1,
    items: mode === 'advanced' ? [...backlogItems] : [],
    topThree: {},
    advanced:
      mode === 'advanced'
        ? {
            rolesScanned: [],
            commsReviewed: false,
            researchItems: [],
            highlightedItemIds: [],
          }
        : undefined,
  };
}

export function getTodaySession(state: MorningBootState): BootSession | undefined {
  const today = todayIsoDate();
  return state.sessions.find((s) => s.date === today && !s.completedAt);
}

export function getOrCreateTodaySession(state: MorningBootState): MorningBootState {
  const existing = getTodaySession(state);
  if (existing) return state;

  const session = createSession(state.settings.mode, state.backlog);
  return {
    ...state,
    sessions: [...state.sessions, session],
  };
}

export function updateSession(
  state: MorningBootState,
  sessionId: string,
  patch: Partial<BootSession>
): MorningBootState {
  return {
    ...state,
    sessions: state.sessions.map((s) => (s.id === sessionId ? { ...s, ...patch } : s)),
  };
}

export function updateSessionItems(
  state: MorningBootState,
  sessionId: string,
  items: BootItem[]
): MorningBootState {
  return updateSession(state, sessionId, { items });
}

export function addItem(
  state: MorningBootState,
  sessionId: string,
  text: string,
  partial?: Partial<BootItem>
): MorningBootState {
  const session = state.sessions.find((s) => s.id === sessionId);
  if (!session) return state;

  const item: BootItem = {
    id: createItemId(),
    text: text.trim(),
    tags: partial?.tags ?? [],
    clarified: partial?.clarified ?? false,
    source: partial?.source ?? 'manual',
    sourceRef: partial?.sourceRef,
    metadata: partial?.metadata,
  };

  return updateSessionItems(state, sessionId, [...session.items, item]);
}

export function updateItem(
  state: MorningBootState,
  sessionId: string,
  itemId: string,
  patch: Partial<BootItem>
): MorningBootState {
  const session = state.sessions.find((s) => s.id === sessionId);
  if (!session) return state;

  const items = session.items.map((item) =>
    item.id === itemId ? { ...item, ...patch } : item
  );
  return updateSessionItems(state, sessionId, items);
}

export function removeItem(
  state: MorningBootState,
  sessionId: string,
  itemId: string
): MorningBootState {
  const session = state.sessions.find((s) => s.id === sessionId);
  if (!session) return state;

  return updateSessionItems(
    state,
    sessionId,
    session.items.filter((i) => i.id !== itemId)
  );
}

export function toggleTag(
  state: MorningBootState,
  sessionId: string,
  itemId: string,
  tag: BootTag
): MorningBootState {
  const session = state.sessions.find((s) => s.id === sessionId);
  if (!session) return state;

  const item = session.items.find((i) => i.id === itemId);
  if (!item) return state;

  const tags = item.tags.includes(tag)
    ? item.tags.filter((t) => t !== tag)
    : [...item.tags, tag];

  return updateItem(state, sessionId, itemId, {
    tags,
    rankWithinTag: undefined,
  });
}

export function setMode(state: MorningBootState, mode: BootMode): MorningBootState {
  const today = getTodaySession({ ...state, settings: { ...state.settings, mode } });
  let next = {
    ...state,
    settings: { ...state.settings, mode },
  };

  if (today && today.mode !== mode) {
    next = updateSession(next, today.id, {
      mode,
      step: 1,
      advanced:
        mode === 'advanced'
          ? today.advanced ?? {
              rolesScanned: [],
              commsReviewed: false,
              researchItems: [],
              highlightedItemIds: [],
            }
          : undefined,
    });
  }

  return next;
}

export function completeSession(state: MorningBootState, sessionId: string): MorningBootState {
  const session = state.sessions.find((s) => s.id === sessionId);
  if (!session) return state;

  const topIds = new Set(
    [session.topThree.a1, session.topThree.t2, session.topThree.g3].filter(Boolean) as string[]
  );

  const backlogItems = session.items
    .filter((item) => !topIds.has(item.id))
    .map((item) => ({ ...item, id: createItemId(), rankWithinTag: undefined }));

  const existingRefs = new Set(state.backlog.map((b) => b.sourceRef).filter(Boolean));
  const newBacklog = [...state.backlog];

  for (const item of backlogItems) {
    if (item.sourceRef && existingRefs.has(item.sourceRef)) continue;
    newBacklog.push(item);
  }

  return {
    ...updateSession(state, sessionId, {
      completedAt: new Date().toISOString(),
      step: getMaxStep(session.mode),
    }),
    backlog: newBacklog,
  };
}

export function importSuggestions(
  state: MorningBootState,
  sessionId: string,
  suggestions: BootItem[]
): MorningBootState {
  const session = state.sessions.find((s) => s.id === sessionId);
  if (!session) return state;

  const merged = mergeImportedItems(session.items, suggestions);
  return updateSessionItems(state, sessionId, merged);
}

export function addToBacklogFromInterrupt(
  state: MorningBootState,
  sessionId: string,
  text: string
): MorningBootState {
  let next = addItem(state, sessionId, text);
  const session = next.sessions.find((s) => s.id === sessionId);
  if (!session) return next;

  const lastItem = session.items[session.items.length - 1];
  const backlogItem: BootItem = {
    ...lastItem,
    id: createItemId(),
  };

  return {
    ...next,
    backlog: [...next.backlog, backlogItem],
  };
}

export function updateAdvanced(
  state: MorningBootState,
  sessionId: string,
  patch: Partial<BootAdvancedState>
): MorningBootState {
  const session = state.sessions.find((s) => s.id === sessionId);
  if (!session) return state;

  const advanced: BootAdvancedState = {
    rolesScanned: session.advanced?.rolesScanned ?? [],
    commsReviewed: session.advanced?.commsReviewed ?? false,
    researchItems: session.advanced?.researchItems ?? [],
    highlightedItemIds: session.advanced?.highlightedItemIds ?? [],
    ...patch,
  };

  return updateSession(state, sessionId, { advanced });
}

export function finalizeRankings(state: MorningBootState, sessionId: string): MorningBootState {
  const session = state.sessions.find((s) => s.id === sessionId);
  if (!session) return state;

  return updateSession(state, sessionId, { topThree: deriveTopThree(session.items) });
}

export function initialState(): MorningBootState {
  return { ...DEFAULT_MORNING_BOOT_STATE };
}
